import React, {useEffect, useRef} from 'react'
import {VStack, HStack, Text, Image, useMultiStyleConfig, StackProps, TextProps, LinkProps, Link, Skeleton, IconButtonProps, FocusLock} from '@chakra-ui/react'
import {Link as RouteLink} from 'react-router-dom'
import {Icon, DarkOverlay, IconButton} from '_uiComponents'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'

type ToastStatus = 'info' | 'warning' | 'success' | 'error' | 'loading' | undefined

interface ToastProps {
    title: string
    productImg?: string
    wishlistLabel: string
    isUserLogged?: boolean
    status: ToastStatus
    closeLabel: string
    children: string
    onClose: () => void
}

/**
 * @name Toast
 *
 * @component
 *
 * @description
 * Displays a message with optional product image and wishlist link.
 *
 * @param {ToastProps} props - The props for the Toast component.
 * @param {string} props.title - Title of the message to be displayed in the toast.
 * @param {string} [props.productImg] - Product image url.
 * @param {string} props.wishlistLabel - Label to be displayed in the wishlist button.
 * @param {boolean} [props.isUserLogged=false] - Indicates if the user is logged or not.
 * @param {ToastStatus} props.status - Status of the toast.
 * @param {string} props.children - Description to be displayed in the toast.
 * @param {function} props.onClose - On close function.
 *
 * @returns {React.FC<ToastProps>} - The Toast component.
 */
const Toast: React.FC<ToastProps> = ({title, productImg, wishlistLabel, isUserLogged = false, status = 'success', closeLabel, children, onClose}) => {
    const styles = useMultiStyleConfig('Toast') as Record<string, StackProps & TextProps & LinkProps & IconButtonProps>
    const {locale} = useMultiSite()

    const closeBtnRef = useRef<HTMLButtonElement | null>(null)
    const prevFocusedElement = useRef<HTMLElement | null>(null)

    const wishlistHref = isUserLogged ? `/${locale.alias}/account?tabID=wishlist` : `/${locale.alias}/wishlist`

    useEffect(() => {
        // Store the previously focused element
        prevFocusedElement.current = document.activeElement as HTMLElement
        // Focus the close button when Toast opens
        closeBtnRef.current?.focus()
        return () => {
            // Restore focus to the previously focused element on close
            prevFocusedElement.current?.focus()
        }
    }, [])

    return (
        <>
            <VStack {...styles.wrapper}>
                <FocusLock persistentFocus={false}>
                    <HStack {...styles.titleWrapper}>
                        <Text {...styles.title}>{title}</Text>
                        <IconButton
                            size="iconButtonMd"
                            ref={closeBtnRef}
                            aria-label={closeLabel}
                            onClick={onClose}
                            icon={<Icon name="IconClose" size="md" />}
                        />
                    </HStack>

                    <HStack {...styles.bodyWrapper}>
                        {productImg && <Image fallback={<Skeleton {...styles.productImageSkeleton} />} src={productImg} alt={title} {...styles.productImage} />}

                        <VStack {...styles.info}>
                            <Text {...styles.message} {...(status === 'error' ? styles.errorMessage : {})}>
                                {children}
                            </Text>

                            {isUserLogged ? (
                                <Link variant="alwaysUnderline" {...styles.wishlistLink} href={wishlistHref} onClick={onClose}>
                                    {wishlistLabel}
                                </Link>
                            ) : (
                                <Link
                                    variant="alwaysUnderline"
                                    as={RouteLink}
                                    {...styles.wishlistLink}
                                    to={{pathname: wishlistHref, state: {scrollToTop: true}}}
                                    onClick={onClose}
                                >
                                    {wishlistLabel}
                                </Link>
                            )}
                        </VStack>
                    </HStack>
                </FocusLock>
            </VStack>
            <DarkOverlay />
        </>
    )
}

export {Toast}
